// Implement a maximum quantity limit for each product to avoid malicious large-number inputs, prevent accidental user entry errors, and ensure quantities remain within a reasonable and valid range.
const MAX_QUANTITY = 100

async function clearCart() {
  try {
    const cartResponse = await fetch('/cart.js')
    if (!cartResponse.ok) {
      return false
    }
    const cart = await cartResponse.json()
    if (cart.items.length === 0) {
      return true
    }
    const updates = cart.items.reduce((acc, item) => {
      acc[item.key] = 0
      return acc
    }, {})
    const updateResponse = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    })
    if (!updateResponse.ok) {
      return false
    }
    await updateResponse.json()
    return true
  } catch (error) {
    console.error('Error clearing cart:', error)
    return false
  }
}

function parseProductsParam(productsParam) {
  if (!productsParam) {
    return []
  }
  let decoded
  try {
    decoded = decodeURIComponent(productsParam)
  } catch (error) {
    console.error('Error decoding products parameter:', error)
    return []
  }
  const terms = decoded.split(',')
  const productMap = new Map()
  for (const term of terms) {
    const trimmed = term.trim()
    if (!trimmed) {
      continue
    }
    const lastColonIndex = trimmed.lastIndexOf(':')
    if (lastColonIndex === -1 || lastColonIndex === 0 || lastColonIndex === trimmed.length - 1) {
      continue
    }
    const retailerId = trimmed.slice(0, lastColonIndex).trim()
    const qtyStr = trimmed.slice(lastColonIndex + 1).trim()
    if (!retailerId) {
      continue
    }
    const qty = Number.parseInt(qtyStr, 10)
    if (Number.isNaN(qty) || qty < 1) {
      continue
    }
    const clampedQty = Math.min(qty, MAX_QUANTITY)
    if (productMap.has(retailerId)) {
      productMap.set(retailerId, productMap.get(retailerId) + clampedQty)
    } else {
      productMap.set(retailerId, clampedQty)
    }
  }
  return Array.from(productMap.entries()).map(([retailerId, qty]) => ({
    retailerId,
    qty: Math.min(qty, MAX_QUANTITY),
  }))
}

function resolveVariantId(retailerId) {
  return retailerId
}

async function addItemsToCart(validatedItems) {
  const items = validatedItems.map((item) => {
    const variantId = resolveVariantId(item.retailerId)
    return {
      id: variantId,
      quantity: item.qty,
    }
  })
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    })
    if (!response.ok) {
      const responseText = await response.text()
      throw new Error(responseText || 'Failed to add items to cart')
    }
    return true
  } catch (error) {
    console.error('Error adding items to cart:', error)
    throw error
  }
}

function buildCheckoutUrl(queryParams) {
  const urlParams = new URLSearchParams()
  if (queryParams.discount) {
    urlParams.set('discount', queryParams.discount)
  }
  if (queryParams.utm_source) {
    urlParams.set('utm_source', queryParams.utm_source)
  }
  if (queryParams.utm_medium) {
    urlParams.set('utm_medium', queryParams.utm_medium)
  }
  if (queryParams.utm_campaign) {
    urlParams.set('utm_campaign', queryParams.utm_campaign)
  }
  if (queryParams.utm_content) {
    urlParams.set('utm_content', queryParams.utm_content)
  }
  if (queryParams.cart_origin) {
    urlParams.set('cart_origin', queryParams.cart_origin)
  }
  if (queryParams.fbclid) {
    urlParams.set('fbclid', queryParams.fbclid)
  }
  const queryString = urlParams.toString()
  return queryString ? `/checkout?${queryString}` : '/checkout'
}

function showError() {
  const loadingEl = document.querySelector('.js-meta-checkout-loading')
  const errorEl = document.querySelector('.js-meta-checkout-error')
  if (loadingEl) {
    loadingEl.classList.add('hidden')
  }
  if (errorEl) {
    errorEl.classList.remove('hidden')
  }
}

async function handleMetaCheckout() {
  try {
    const urlParams = new URLSearchParams(window.location.search)
    const productsParam = urlParams.get('products')
    const validatedItems = parseProductsParam(productsParam)
    if (validatedItems.length === 0) {
      showError()
      return
    }
    const queryParams = {
      discount: urlParams.get('discount') || urlParams.get('coupon') || null,
      utm_source: urlParams.get('utm_source') || null,
      utm_medium: urlParams.get('utm_medium') || null,
      utm_campaign: urlParams.get('utm_campaign') || null,
      utm_content: urlParams.get('utm_content') || null,
      cart_origin: urlParams.get('cart_origin') || null,
      fbclid: urlParams.get('fbclid') || null,
    }
    await clearCart()
    await addItemsToCart(validatedItems)
    const checkoutUrl = buildCheckoutUrl(queryParams)
    window.location.href = checkoutUrl
  } catch (error) {
    console.error('Error processing meta checkout:', error)
    showError()
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', handleMetaCheckout)
} else {
  handleMetaCheckout()
}

